import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useRouteError,
} from 'react-router-dom'
import { useAuth } from '@/providers/AuthProvider'
import { AppShell } from '@/components/layout/AppShell'
import { Button, Spinner } from '@/components/ui'

const LoginPage = lazy(() => import('@/features/auth/LoginPage'))
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'))
const ComposerPage = lazy(() => import('@/features/composer/ComposerPage'))
const CalendarPage = lazy(() => import('@/features/calendar/CalendarPage'))
const PostsPage = lazy(() => import('@/features/posts/PostsPage'))
const AccountsPage = lazy(() => import('@/features/accounts/AccountsPage'))
const MetaLoginCallback = lazy(() => import('@/features/accounts/MetaLoginCallback'))
const LibraryPage = lazy(() => import('@/features/library/LibraryPage'))
const InsightsPage = lazy(() => import('@/features/insights/InsightsPage'))
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'))
const NotFoundPage = lazy(() => import('@/features/misc/NotFoundPage'))

function FullPageLoader({ label }: { label: string }) {
  return (
    <div
      className="flex min-h-[60vh] flex-col items-center justify-center gap-3"
      role="status"
      aria-live="polite"
    >
      <Spinner size="lg" />
      <p className="text-sm text-[var(--text-secondary)]">{label}</p>
    </div>
  )
}

const RELOAD_GUARD = 'socialhub.recarga-por-versao'

/**
 * Depois de uma nova versão ir ao ar, quem estava com a aba aberta ainda pede
 * os arquivos antigos — que já não existem. O navegador falha ao carregar o
 * pedaço da tela e o React Router mostra a própria tela de erro, cheia de
 * jargão. Aqui a gente detecta esse caso e recarrega uma única vez.
 */
function isVersaoDesatualizada(error: unknown): boolean {
  const texto = error instanceof Error ? `${error.name} ${error.message}` : String(error)
  return /dynamically imported module|Importing a module script failed|error loading dynamically|ChunkLoadError/i.test(
    texto,
  )
}

function RouteErrorBoundary() {
  const error = useRouteError()
  const desatualizado = isVersaoDesatualizada(error)

  useEffect(() => {
    if (!desatualizado) return
    // O guarda impede laço infinito se a recarga não resolver.
    try {
      if (sessionStorage.getItem(RELOAD_GUARD)) return
      sessionStorage.setItem(RELOAD_GUARD, '1')
    } catch {
      return
    }
    window.location.reload()
  }, [desatualizado])

  if (desatualizado) {
    return <FullPageLoader label="Uma nova versão ficou disponível. Atualizando…" />
  }

  const detalhe = error instanceof Error ? error.message : String(error)

  return (
    <div role="alert" className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="sh-card w-full max-w-md p-8 text-center">
        <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
          Esta tela não pôde ser aberta
        </h1>
        <p className="mt-2 text-sm leading-5 text-[var(--text-secondary)]">
          O restante do aplicativo continua funcionando, e nada do que você salvou foi perdido.
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <Button onClick={() => window.location.reload()} fullWidth>
            Recarregar a página
          </Button>
          <Button variant="secondary" onClick={() => window.location.assign('/')} fullWidth>
            Voltar ao painel
          </Button>
        </div>
        <details className="mt-4 text-left">
          <summary className="cursor-pointer text-xs text-[var(--text-muted)]">
            Detalhes técnicos
          </summary>
          <pre className="sh-scroll-thin mt-2 max-h-40 overflow-auto rounded-md bg-[var(--surface-inset)] p-3 text-xs text-[var(--text-secondary)]">
            {detalhe}
          </pre>
        </details>
      </div>
    </div>
  )
}

function RootLayout() {
  // Chegou até aqui: a versão carregada está íntegra, o guarda pode ser limpo.
  useEffect(() => {
    try {
      sessionStorage.removeItem(RELOAD_GUARD)
    } catch {
      // storage indisponível: sem guarda, a recarga simplesmente não repete
    }
  }, [])

  return (
    <Suspense fallback={<FullPageLoader label="Carregando…" />}>
      <Outlet />
    </Suspense>
  )
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth()
  const location = useLocation()

  if (loading) return <FullPageLoader label="Verificando sua sessão…" />
  if (!session) return <Navigate to="/entrar" replace state={{ from: location.pathname }} />
  return <>{children}</>
}

function LoginRoute() {
  const { session, loading } = useAuth()
  if (loading) return <FullPageLoader label="Verificando sua sessão…" />
  if (session) return <Navigate to="/" replace />
  return <LoginPage />
}

function ProtectedShell() {
  return (
    <RequireAuth>
      <AppShell />
    </RequireAuth>
  )
}

/**
 * Data router (em vez de `<BrowserRouter>`): é o que habilita `useBlocker`,
 * usado pelo editor para avisar antes de sair com alterações não salvas.
 */
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/entrar', element: <LoginRoute /> },
      {
        element: <ProtectedShell />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'criar', element: <ComposerPage /> },
          { path: 'criar/:postId', element: <ComposerPage /> },
          { path: 'calendario', element: <CalendarPage /> },
          { path: 'publicacoes', element: <PostsPage /> },
          { path: 'contas', element: <AccountsPage /> },
          // Retorno do diálogo da Meta: fica dentro da área autenticada porque
          // a troca do `code` precisa da sessão e do workspace do usuário.
          { path: 'contas/callback', element: <MetaLoginCallback /> },
          { path: 'biblioteca', element: <LibraryPage /> },
          { path: 'desempenho', element: <InsightsPage /> },
          { path: 'ajustes', element: <SettingsPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
])

/** Ponto de entrada do roteamento — o único export deste arquivo. */
export function AppRouter() {
  return <RouterProvider router={router} />
}
